import { DateTime } from 'luxon';
import { Button, Input } from '@flixbus/honeycomb-react';
import { useVoucherFormContext } from '../../Context';
import { BlackoutPeriodRule } from '../../../../types/Rules';
import { Icon, IconClose } from '@flixbus/honeycomb-icons-react';

const BlackoutTo = ({ index }: { index: number }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  // Get the specific blackout period rule for this index
  const ruleValue = rules.find(
    r => r.type === 'blackout_period' && (r as BlackoutPeriodRule).id === `blackout_${index}`,
  ) as BlackoutPeriodRule | undefined;

  const dateObject = ruleValue?.end ? DateTime.fromISO(ruleValue.end) : null;

  // Set minimum date to the start date (same day or later)
  const startDateObject = ruleValue?.start ? DateTime.fromISO(ruleValue.start) : null;
  const minDate = startDateObject
    ? startDateObject.setLocale('de').toFormat('yyyy-MM-dd')
    : undefined;

  const handleClear = () => {
    const ruleId = `blackout_${index}`;

    // If there's no start date, remove the rule entirely
    if (!ruleValue?.start) {
      handleRuleChange({ type: 'blackout_period', id: ruleId } as BlackoutPeriodRule, true, ruleId);
    } else {
      // Keep the rule but remove the end date
      handleRuleChange(
        {
          type: 'blackout_period',
          id: ruleId,
          start: ruleValue.start,
        } as BlackoutPeriodRule,
        false,
        ruleId,
      );
    }
  };

  return (
    <Input
      name={`rule-blackout-to-input-${index}`}
      id={`rule-blackout-to-input-${index}`}
      label={<>To</>}
      type='date'
      min={minDate}
      required
      value={dateObject ? dateObject.setLocale('de').toFormat('yyyy-MM-dd') : ''}
      onChange={e => {
        const { value } = e.target;
        const ruleId = `blackout_${index}`;

        if (value === '') {
          // If the value is empty and there's no start date, remove the rule entirely
          if (!ruleValue?.start) {
            handleRuleChange(
              { type: 'blackout_period', id: ruleId } as BlackoutPeriodRule,
              true,
              ruleId,
            );
          } else {
            // Keep the rule but remove the end date
            handleRuleChange(
              {
                type: 'blackout_period',
                id: ruleId,
                start: ruleValue.start,
              } as BlackoutPeriodRule,
              false,
              ruleId,
            );
          }
        } else {
          // Update or create the rule with the new end date
          const date = new Date(value);
          handleRuleChange(
            {
              type: 'blackout_period',
              id: ruleId,
              start: ruleValue?.start,
              end: DateTime.fromJSDate(date)
                .endOf('day')
                .startOf('second')
                .toISO({ includeOffset: true, suppressMilliseconds: true }) as string,
            } as BlackoutPeriodRule,
            false,
            ruleId,
          );
        }
      }}
      iconRight={
        dateObject ? (
          <Button aria-label='Clear end date field' onClick={handleClear}>
            <Icon appearance='primary' InlineIcon={IconClose} />
          </Button>
        ) : undefined
      }
    />
  );
};

export default BlackoutTo;
